import clsx from 'clsx';
import { useParams, usePathname } from 'next/navigation';
import { Popover } from '@headlessui/react';
import useTranslation from 'next-translate/useTranslation';

// Utils
import { getIsHeaderManageProfilesDisabled } from '@/utils/layout/getIsHeaderManageProfilesDisabled';
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';

// Components
import Icon from '@/components/base/Icon';
import { ButtonStyle } from '@/components/base/Button';

// Types
import type { PopoverChildrenProps } from '@/components/base/Popover';

type RecapSocialProfilesProps = Pick<PopoverChildrenProps, 'setReferenceElement'>;

export function RecapSocialProfiles({ setReferenceElement }: RecapSocialProfilesProps) {
    const { t } = useTranslation('layout_platform');
    const pathname = usePathname();
    const params = useParams();
    const isDisabled = getIsHeaderManageProfilesDisabled({ pathname, params });
    const { activatedSpGroups } = useSPGroups({ revalidateIfStale: false });
    const { activeGroupId, activeMultiSelectionProfiles } = useProfilesStore((state) => state);

    const numberSelectedProfiles = () => {
        if (activeMultiSelectionProfiles?.socialProfileIds) {
            return activeMultiSelectionProfiles.socialProfileIds.length;
        }

        if (activeGroupId) {
            return activatedSpGroups
                .find((group) => group.id === activeGroupId)
                ?.social_profiles.filter((profile) => profile.active).length;
        }

        return 1;
    };

    return (
        <Popover.Button
            ref={setReferenceElement}
            disabled={isDisabled}
            data-testid="button-manage-profiles-popover"
            className={clsx(
                [
                    'flex !min-w-fit items-center gap-2 focus:ring-offset-0',
                    'h-8 px-2 py-2 sm:h-12 sm:px-4',
                    'disabled:opacity-40'
                ],
                {
                    [ButtonStyle['whiteNoBorder']]: isDisabled,
                    [ButtonStyle['whiteOpacity40']]: !isDisabled
                }
            )}>
            <Icon icon="single-profile" className="text-icoBlue" />
            <span className="flex items-center gap-1">
                <span className="hidden text-xs text-icoBlack dark:text-white sm:inline">
                    {t('manage_profiles.button_popover')}
                </span>
                <span className="flex h-4 min-w-[20px] items-center justify-center rounded-full bg-icoBlue px-1 text-xxs leading-none text-white">
                    {numberSelectedProfiles()}
                </span>
            </span>
            <Icon
                icon="chevron-bottom"
                className="text-xs text-icoGray-400 dark:text-icoDarkMode-wolf"
            />
        </Popover.Button>
    );
}
